import { BufferGeometry, CanvasTexture, Color, Float32BufferAttribute, Material, Points, PointsMaterial } from "three";
import SceneGeometry from "./SceneGeometry";
import EditHandleData from "../sceneObjectData/EditHandleData";
import SceneObjectData from "../sceneObjectData/SceneObjectData";

export default class EditHandleGeometry extends SceneGeometry<EditHandleData> {

    private static readonly HANDLE_SIZE_PX = 10;
    private static readonly HOVER_SIZE_PX = EditHandleGeometry.HANDLE_SIZE_PX * 1.5;
    private static readonly NORMAL_FILL_COLOR = 0x1565C0;
    private static readonly HOVER_FILL_COLOR = 0xff0000;
    private static readonly HOVER_TRANSITION_MS = 150;
    private static readonly BORDER_PX = 3;
    private static readonly TEXTURE_SIZE = 32;

    private pointsGeometry: BufferGeometry;
    private material: PointsMaterial;
    private points: Points;
    private canvas: HTMLCanvasElement;
    private ctx: CanvasRenderingContext2D;
    private texture: CanvasTexture;
    private currentFillColor: Color = new Color(EditHandleGeometry.NORMAL_FILL_COLOR);
    private hovered = false;
    private hoverAnimationFrameId: number | null = null;

    private static createTexture(): { canvas: HTMLCanvasElement; ctx: CanvasRenderingContext2D; texture: CanvasTexture } {
        const size = EditHandleGeometry.TEXTURE_SIZE;
        const canvas = document.createElement('canvas');
        canvas.width = size;
        canvas.height = size;
        const ctx = canvas.getContext('2d')!;
        const texture = new CanvasTexture(canvas);
        return { canvas, ctx, texture };
    }

    private drawFill(fillColor: Color): void {
        const size = EditHandleGeometry.TEXTURE_SIZE;
        const border = EditHandleGeometry.BORDER_PX;

        // White border
        this.ctx.fillStyle = '#ffffff';
        this.ctx.fillRect(0, 0, size, size);

        // Fill color
        this.ctx.fillStyle = `#${fillColor.getHexString()}`;
        this.ctx.fillRect(border, border, size - border * 2, size - border * 2);

        this.currentFillColor = fillColor.clone();
        this.texture.needsUpdate = true;
    }

    constructor() {
        super();

        this.pointsGeometry = new BufferGeometry();
        this.pointsGeometry.setAttribute('position', new Float32BufferAttribute([0, 0, 0], 3));

        const { canvas, ctx, texture } = EditHandleGeometry.createTexture();
        this.canvas = canvas;
        this.ctx = ctx;
        this.texture = texture;
        this.drawFill(new Color(EditHandleGeometry.NORMAL_FILL_COLOR));

        this.material = new PointsMaterial({
            color: 0xffffff,
            size: EditHandleGeometry.HANDLE_SIZE_PX,
            sizeAttenuation: false,
            depthTest: false,
            depthWrite: false,
            map: this.texture,
            transparent: true,
        });

        this.points = new Points(this.pointsGeometry, this.material);
        this.points.renderOrder = 999;
        this.group.renderOrder = 999;
        this.group.add(this.points);
    }

    getMaterials(): Material[] {
        return [this.material];
    }

    setHover(hovered: boolean): void {
        if (this.hovered === hovered) return;
        this.hovered = hovered;

        const targetSize = hovered ? EditHandleGeometry.HOVER_SIZE_PX : EditHandleGeometry.HANDLE_SIZE_PX;
        const targetFillColor = new Color(hovered ? EditHandleGeometry.HOVER_FILL_COLOR : EditHandleGeometry.NORMAL_FILL_COLOR);
        const startSize = this.material.size;
        const startFillColor = this.currentFillColor.clone();
        const startTime = performance.now();

        if (this.hoverAnimationFrameId !== null) {
            cancelAnimationFrame(this.hoverAnimationFrameId);
        }

        const step = (now: number) => {
            const t = Math.min(1, (now - startTime) / EditHandleGeometry.HOVER_TRANSITION_MS);
            this.material.size = startSize + (targetSize - startSize) * t;
            this.drawFill(startFillColor.clone().lerp(targetFillColor, t));

            if (t < 1) {
                this.hoverAnimationFrameId = requestAnimationFrame(step);
            } else {
                this.hoverAnimationFrameId = null;
            }
        };
        this.hoverAnimationFrameId = requestAnimationFrame(step);
    }

    dispose(): void {
        if (this.hoverAnimationFrameId !== null) {
            cancelAnimationFrame(this.hoverAnimationFrameId);
            this.hoverAnimationFrameId = null;
        }
        super.dispose();
    }

    protected updateGeometry(data: SceneObjectData, forceUpdate: boolean): boolean {
        const handleData = data as EditHandleData;

        if (!forceUpdate && this.data && this.data.position.equals(handleData.position)) {
            return false;
        }

        this.points.position.copy(handleData.position);
        this.data = handleData;
        return true;
    }
}
